import React, { useRef, useState, useEffect, useMemo } from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import * as d3 from "d3";
import { grey } from "@mui/material/colors";
import { calculateGrowth } from "components/Reports/data";

/**
 * SalesStackedBars.jsx
 * Fully responsive stacked bar chart
 * - Auto color mapping by brand
 * - Handles large values gracefully
 * - Generates legend dynamically
 */

const defaultColors = ["#6B7A8F", "#D9C7B4", "#B4CDE6", "#E8B4B8", "#C9A3A0", "#B5D99C", "#A9A9D9"];

function Legend({ brandColors }) {
	const items = Object.entries(brandColors);
	if (items.length === 0) return null;

	return (
		<Stack direction="row" flexWrap="wrap" rowGap="5px" columnGap="5px" width="80%" margin="auto" pb={2}>
			{items.map(([label, color], idx) => (
				<Stack key={idx} direction="row" alignItems="center" spacing={0.5}>
					<Box
						sx={{
							width: 10,
							height: 10,
							bgcolor: color,
							borderRadius: 0.5,
							boxShadow: "0 0 2px rgba(0,0,0,0.2)",
						}}
					/>
					<Typography
						sx={{
							fontSize: 12,
							color: grey[800],
							fontWeight: 500,
							userSelect: "none",
						}}>
						{label}
					</Typography>
				</Stack>
			))}
		</Stack>
	);
}

// ---------- Utility ----------
function getLuminanceFromRgb(r, g, b) {
	const RsRGB = r / 255;
	const GsRGB = g / 255;
	const BsRGB = b / 255;
	const [R, G, B] = [RsRGB, GsRGB, BsRGB].map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
	return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function bestTextColorForBackground(colorStr) {
	const { r, g, b } = d3.rgb(colorStr);
	const L = getLuminanceFromRgb(r, g, b);
	const contrastWhite = 1.05 / (L + 0.05);
	const contrastBlack = (L + 0.05) / 0.05;
	return contrastWhite >= contrastBlack ? "#ffffff" : "#000000";
}

const tooltipDataShow = (tooltipData, valRoundOffFun, isDiscountType, total) =>{
	console.log("tooltipDataShow", tooltipData)
	const ty_value = valRoundOffFun(tooltipData?.value)
	const ly_value = valRoundOffFun(tooltipData?.ly_ach_value)
	const growth = calculateGrowth(tooltipData?.ly_ach_value, tooltipData?.value)
	const contri = ((tooltipData?.value / total) * 100).toFixed(1)
	return(
		<Stack>
			<Typography fontSize={"var(--font-size-tooltip)"}>{tooltipData?.text} <span style={{fontWeight: 600}}>({contri}%)</span></Typography>
		  <Stack display={"flex"} flexDirection={"row"}>
			<Typography sx={{ fontSize:"var(--font-size-tooltip)", marginRight: "2px"}}>TY:</Typography>
			<Typography sx={{ fontSize:"var(--font-size-tooltip)"}}>{ty_value || 0}</Typography>
			<Typography sx={{ fontSize:"var(--font-size-tooltip)", marginLeft: "3px", fontWeight: 600, color: isDiscountType ? growth > 0 ? "red": "green" : growth > 0 ? "green" : "red"}}>({growth || 0}%)</Typography>
		  </Stack>
		  <Stack display={"flex"} flexDirection={"row"}>
			<Typography sx={{ fontSize:"var(--font-size-tooltip)", marginRight: "2px"}}>LY:</Typography>
			<Typography sx={{ fontSize:"var(--font-size-tooltip)"}}>{ly_value || 0}</Typography>
		  </Stack>

		</Stack>
	)
  }

// ---------- Main Component ----------
export default function SalesStackedBars({ sx, yLabel = "", dataSet, colors = defaultColors, formatBarTotalValue = (val) => val, valRoundOffFun, isDiscountType}) {
	const containerRef = useRef(null);
	const [dimensions, setDimensions] = useState({ width: 300, height: 300 });

	// Responsive observer
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const updateSize = () => {
			const rect = el.getBoundingClientRect();
			setDimensions({ width: rect.width, height: rect.height + 35 });
		};
		updateSize();
		const observer = new ResizeObserver(updateSize);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const { width, height } = dimensions;

	// -------- Color Mapping (brand -> color) --------
	const brandColors = useMemo(() => {
		const brands = new Set();
		dataSet?.forEach((d) => d.segments.forEach((seg) => brands.add(seg.brand)));
		const brandList = Array.from(brands);
		return Object.fromEntries(brandList.map((b, i) => [b, colors[i % colors.length]]));
	}, [dataSet, colors]);

	// -------- Scale --------
	const totals = dataSet.map((d) => d.segments.reduce((sum, seg) => sum + seg.value, 0));
	const maxTotal = d3.max(totals) || 1;

	const scaleY = d3
		.scaleLinear()
		.domain([0, maxTotal])
		.range([0, height]);

	const barWidth = Math.max(width / (dataSet.length * 2), 25);
	const gap = (width - barWidth * dataSet.length) / (dataSet.length + 1);

	return (
		<Box sx={{ ...sx }}>
			<Legend brandColors={brandColors} />
			<Box
				ref={containerRef}
				sx={{
					position: "relative",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "flex-end",
					width: "100%",
					height: "100%",
					boxSizing: "border-box",
					overflow: "hidden",
					pl: 2,
				}}>
				{/* Y-axis Label */}
				<Typography
					sx={{
						position: "absolute",
						left: -10,
						top: "50%",
						transform: "rotate(-90deg) translateY(50%)",
						transformOrigin: "center",
						fontSize: 10,
						color: grey[600],
						whiteSpace: "nowrap",
						userSelect: "none",
						fontWeight: 600,
					}}>
					{yLabel}
				</Typography>

				{/* Bars */}
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-end",
						justifyContent: "center",
						width: "100%",
						flexGrow: 1,
						pl: 3,
						pb: 0.5,
						mt: "auto",
					}}>
					{dataSet.map((d, i) => {
						const totalValue = totals[i];
						return (
							<Box
								key={i}
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									position: "relative",
									mx: `${gap / 2}px`,
								}}>
								{/* total label */}
								<Typography
									sx={{
										fontSize: 10,
										lineHeight: 1,
										mb: 0.2,
										color: grey[800],
										fontWeight: 500,
									}}>
									{formatBarTotalValue(totalValue)}
								</Typography>

								{/* stacked bar */}
								<Box
									sx={{
										width: barWidth,
										height: scaleY(totalValue),
										display: "flex",
										flexDirection: "column-reverse",
										borderRadius: 0.8,
										overflow: "hidden",
										boxShadow: "0 0 2px rgba(0,0,0,0.1)",
										transition: "all 0.3s ease",
									}}>
									{d.segments.map((seg, j) => {
										const color = brandColors[seg.brand] || colors[j % colors.length];
										const textColor = bestTextColorForBackground(color);
										const segHeightRaw = scaleY(seg.value);

										// ensure a minimum visible height (e.g. 6px)
										const minVisibleHeight = 6;
										const segHeight = Math.max(segHeightRaw, minVisibleHeight);

										// show text only if tall enough (e.g. >20px)
										const minHeightForText = 20;
										// const showText = segHeightRaw > minHeightForText;
										const showText = false;

										return (
											<Tooltip
												key={j}
												title={tooltipDataShow(seg, valRoundOffFun, isDiscountType, totalValue)}
												slotProps={{
												tooltip: {
													sx: {
													backgroundColor: "var(--color-tooltip-background)",
													color: "var(--color-tooltip)",
													// border: "0.2px solid black",
													}
												}
												}} followCursor
												enterDelay={100}
												disableInteractive>
												<Box
													sx={{
														height: segHeight,
														bgcolor: color,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														px: 0.3,
														transition: "height 0.3s ease",
														cursor: !showText ? "pointer" : "default",
													}}>
													{showText && (
														<Typography
															sx={{
																fontSize: Math.max(7, barWidth * 0.3),
																color: textColor,
																textAlign: "center",
																lineHeight: 1.1,
																fontWeight: 500,
																pointerEvents: "none",
															}}>
															{seg.text}
														</Typography>
													)}
												</Box>
											</Tooltip>
										);
									})}
								</Box>

								{/* x-axis label */}
								<Typography
									sx={{
										fontSize: 10,
										mt: 0.2,
										color: grey[700],
										textAlign: "center",
										height: 12,
										textTransform: "capitalize",
									}}>
									{d.label.replace("_", " ")}
								</Typography>
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
}
